import { defer, isObservable, MonoTypeOperatorFunction, Observable, pipe } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { log } from '@core/services/logging/logger';
import type { FileOperation } from '@core/services/logging/logAttributes';

export const fileLog = log.withPrefix("[file]");

const WRITE_OPERATIONS: ReadonlyArray<FileOperation> = ['upload', 'copy', 'delete'];

interface LoggedFile {
    type: string;
    fileSize?: number;
}

/**
 * Logs a file operation once it completes, fails or is cancelled, with its duration:
 * - writes (`upload`, `copy`, `delete`) at `info`, sent to Sentry
 * - reads (`download`, `list`, `metadata`) at `debug`, console only
 * - failures at `error`, cancelled writes (unsubscribed before any result) at `warn`
 */
export function logFileOperation<T>(operation: FileOperation, file: LoggedFile, destination?: LoggedFile): MonoTypeOperatorFunction<T> {
    return (source$: Observable<T>) => defer(() => {
        const startTime = Date.now();
        let emittedCount = 0;
        let isSettled = false;

        const operationLog = () => fileLog.child()
            .withContext({
                'file.operation': operation,
                'file.driver': file.type,
                'file.path': describeFile(file),
                'file.destination_path': destination ? describeFile(destination) : undefined,
                'file.size': file.fileSize,
            })
            .withMetadata({
                'file.duration_ms': Date.now() - startTime,
                'file.count': operation === 'list' ? emittedCount : undefined,
            });
        const isWrite = WRITE_OPERATIONS.includes(operation);
        const logSuccess = () => {
            isSettled = true;
            if (isWrite) {
                operationLog().info(`file.${operation}`);
            } else {
                operationLog().debug(`file.${operation}`);
            }
        };
        // Single result operations are often consumed with `yield* call()` / `firstValueFrom()`, which unsubscribe
        // right after the first value without waiting for the completion: the first value is the success.
        const isSingleResult = operation !== 'list';

        return pipe(
            tap<T>({
                next: () => {
                    emittedCount++;
                    if (isSingleResult && !isSettled) {
                        logSuccess();
                    }
                },
                complete: () => {
                    if (!isSettled) {
                        logSuccess();
                    }
                },
                error: (error: unknown) => {
                    isSettled = true;
                    operationLog().withError(error).error(`file.${operation}.failed`);
                },
            }),
            finalize<T>(() => {
                if (isSettled) {
                    return;
                }
                if (isWrite) {
                    operationLog().warn(`file.${operation}.cancelled`);
                } else {
                    operationLog().debug(`file.${operation}.cancelled`);
                }
            }),
        )(source$);
    });
}

/**
 * `download()` returns a lazy Data: the file is only read when one of its `as*()` accessors is subscribed,
 * so the download is logged at that time.
 */
export function logDataDownload<D extends object>(data: D, file: LoggedFile): D {
    return new Proxy(data, {
        get(target, property) {
            const value = Reflect.get(target, property, target);
            if (typeof value !== 'function') {
                return value;
            }
            if (typeof property !== 'string' || !property.startsWith('as')) {
                return value.bind(target);
            }
            return (...args: unknown[]) => {
                const result = value.apply(target, args);
                return isObservable(result) ? result.pipe(logFileOperation('download', file)) : result;
            };
        },
    });
}

/**
 * Readable location of a file or path, e.g. `S3File(S3ProjectPath(…),"<patchId>/<entityId>/photo.jpg")`.
 * URL query strings (signatures) are removed.
 */
function describeFile(file: LoggedFile): string {
    const description = String(file);
    if (description !== '[object Object]') {
        return description;
    }
    const fields = Object.entries(file)
        .filter(([, value]) => ['string', 'number'].includes(typeof value))
        .map(([key, value]) => `${key}=${typeof value === 'string' ? withoutQueryString(value) : value}`);
    return `${file.constructor.name}(${fields.join(', ')})`;
}

function withoutQueryString(value: string): string {
    return /^https?:\/\//.test(value) ? value.split('?')[0] : value;
}
